import React, { useEffect, useState, useRef } from "react";
import {
  Button,
  MantineProvider,
  Card,
  Text,
  Group,
  Title,
  Transition,
  Divider,
  Container,
  Stack,
  Burger,
  Drawer,
  SegmentedControl,
  Center,
  Box,
} from "@mantine/core";
import {
  IconArrowRight,
  IconDownloadOff,
  IconChecklist,
  IconTools,
  IconPaperclip,
  IconLogin,
  IconFileDescription,
  IconLock,
  IconFileSearch,
} from "@tabler/icons-react";
import { useIntersection, useDisclosure } from "@mantine/hooks";
import styles from "./SplashLanding.module.css";
import { ContactModal } from "./ContactModal";
import ParticleAnimation from "./ParticleAnimation";
import { Footer } from "./Footer/Footer";
import { useLocation } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { Header } from "./Header/Header";

const theme = {
  components: {
    Button: {
      styles: {
        root: {
          transition: "background-color 0.3s ease-in-out",
          "&:hover": {
            backgroundColor: "rgba(15, 45, 45, 0.75)",
            color: "white",
          },
        },
      },
    },
  },
};

const SplashLanding = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [visibleFeatures, setVisibleFeatures] = useState({});
  const featuresRef = useRef(null);

  const location = useLocation(); // Initialize useLocation

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("subscribed") === "true") {
      // Trigger an analytics event
      // Example: analytics.track('MailingListSubscription', { user: 'anonymous' });
      // Optionally, display a success notification
    }
  }, [location]);

  const [activeDemo, setActiveDemo] = useState("SAFE");

  const demoConfigs = {
    SAFE: {
      steps: [
        {
          subject: "FWD: TechVentures Financing Documents",
          content:
            "Please draft a full set of the primary NVCA documents based on the term sheet attached from John.\n\nBob Johnson\nPartner\nJohnson, Johnson & Johnson LP",
          quoted:
            "---------- Forwarded message ---------\nHey, \nGreat news! TechVentures LP has agreed on terms, see terms attached. Can you pull together the financing documents?\nThank you,\nJohn Bobson\nCo-Founder\nXYZ Startup, Inc.",
          attachments: ["TechVentures LP Term Sheet.docx"],
        },
        {
          subject: "RE: FWD: TechVentures Financing Documents",
          content:
            "I've prepared the documents as requested. Please find attached:\n\n1. NVCA Charter\n2. Stock Purchase Agreement\n3. Investors' Rights Agreement\n4. Voting Agreement\n5. Right of First Refusal and Co-Sale Agreement \n\nRegards,\nAI Associate",
          attachments: [
            "NVCA Charter.docx",
            "NVCA Charter Redline [v Form].pdf",
            "Stock Purchase Agreement.docx",
            "Stock Purchase Agreement Redline [v Form].pdf",
            "Investors Rights Agreement.docx",
            "Investors Rights Agreement Redline [v Form].pdf",
            "Voting Agreement.docx",
            "Voting Agreement Redline [v Form].pdf",
            "Right of First Refusal and Co-Sale Agreement.docx",
            "Right of First Refusal and Co-Sale Agreement Redline [v Form].pdf",
          ],
        },
      ],
      descriptions: [
        "Forward an email request directly to the Socius Legal AI Assistant",
        "Receive a draft DOCX with redline against the standard form",
      ],
    },
    ISSUES_LIST: {
      steps: [
        {
          subject: "Issues List",
          content:
            "Hey Socius,\n\nWe just received comments back from the Seller's counsel on the draft APA.  Can you please take a look and write up an issues list?\n\nThanks",
          quoted: "",
          attachments: ["APA [Seller Comments].docx", "APA [Buyer Draft].docx"],
        },
        {
          subject: "RE: Issues List",
          content:
            "I've prepared the following Issues List as requested:\n\n1. APA - Issues List [Draft]\n\nRegards,\nAI Associate",
          attachments: ["APA - Issues List [Draft].docx"],
        },
      ],
      descriptions: [
        "Email a request to the Socius Legal AI Assistant, including attachments",
        "Receive an Issues List in DOCX format",
      ],
    },
    REDLINE: {
      steps: [
        {
          subject: "Redline Request",
          content:
            "Can you run a quick comparison?  The Buyer's comments are attached, run them against the Seller's draft.\n\nThanks",
          quoted: "",
          attachments: ["APA [Buyer Comments].docx", "APA [Seller Draft].docx"],
        },
        {
          subject: "RE: Redline Request",
          content:
            "I've prepared the attached Redline as requested.\n\nRegards,\nAI Associate",
          attachments: ["APA - Redline [Draft].docx"],
        },
      ],
      descriptions: [
        "Send two documents and request a comparison",
        "Receive a Litera Change-Pro PDF Redline",
      ],
    },
    QA: {
      steps: [
        {
          subject: "Question on ROFR",
          content:
            "Hey - One of the founders wants to transfer 5% of their holdings to a trust for the benefit of her children. Is this permissible under the ROFR agreement (attached)?\n\nThanks",
          quoted: "",
          attachments: ["ROFR Agreement.docx"],
        },
        {
          subject: "RE: Question on ROFR",
          content:
            'Yes, this transfer is permissible.  Section 3.1(d) of the ROFR agreement exempts transfers of stock by a Key Holder "for bona fide estate planning purposes" to a trust for the benefit of their children.  The Key Holder must deliver prior written notice to the Investors of the transfer, and the shares of stock held by the trust will remain subject to the terms of the ROFR agreement.\n\nPlease let me know if you need any further clarification or have additional questions.\n\nBest regards,\nAI Associate',
          //
          attachments: [],
        },
      ],
      descriptions: [
        "Ask a question to the Socius Legal AI Assistant",
        "Receive a response, including citations from attached documents.",
      ],
    },
  };

  const [demoSteps, setDemoSteps] = useState(demoConfigs[activeDemo].steps);

  useEffect(() => {
    setDemoSteps(demoConfigs[activeDemo].steps);
  }, [activeDemo]);

  const [opened, setOpened] = useState(false);

  const toggle = () => setOpened((o) => !o);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setVisibleFeatures((prev) => ({
            ...prev,
            [entry.target.dataset.featureIndex]: entry.isIntersecting,
          }));
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -10% 0px" },
    );

    const featureCards = featuresRef.current.querySelectorAll(
      `.${styles.feature}`,
    );
    featureCards.forEach((card, index) => {
      card.dataset.featureIndex = index;
      observer.observe(card);
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  const demoRef = useRef(null);

  const renderDemoStep = (step, index) => (
    <React.Fragment key={index}>
      <Title order={3} pt="sm" fw={500} style={{ textAlign: "center" }}>
        {demoConfigs[activeDemo].descriptions[index]}
      </Title>
      <Card
        className={styles.demoCard}
        shadow="sm"
        padding="lg"
        radius="md"
        withBorder
      >
        <Group position="apart" mt="xs" mb="xs" ml="xs">
          <Text c="dimmed" size="sm" fw={400}>
            Subject:
          </Text>
          <Text size="md" fw={500}>
            {step.subject}
          </Text>
        </Group>
        <div className={styles.contentArea}>
          <Text size="md" style={{ whiteSpace: "pre-wrap" }}>
            {step.content}
          </Text>
          {step.quoted && (
            <Text
              size="sm"
              c="dimmed"
              style={{ whiteSpace: "pre-wrap", marginTop: "10px" }}
            >
              {step.quoted}
            </Text>
          )}
        </div>
        {step.attachments.length > 0 && (
          <Group mt="md" gap="xs">
            {step.attachments.slice(0, 4).map((attachment, i) => (
              <div
                key={`${index}-${i}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "8px 12px",
                  backgroundColor: "var(--mantine-color-gray-1)",
                  borderRadius: "4px",
                  fontSize: "12px",
                  fontWeight: "500",
                }}
              >
                <IconPaperclip size={14} style={{ marginRight: "4px" }} />
                {attachment}
              </div>
            ))}
            {step.attachments.length > 4 && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "8px 12px",
                  backgroundColor: "var(--mantine-color-gray-1)",
                  borderRadius: "4px",
                  fontSize: "12px",
                  fontWeight: "500",
                  color: "var(--mantine-color-gray-6)",
                }}
              >
                +{step.attachments.length - 4} more attachments
              </div>
            )}
          </Group>
        )}
      </Card>
    </React.Fragment>
  );

  const renderMobileMenu = () => (
    <Drawer
      opened={opened}
      onClose={toggle}
      title="Menu"
      padding="xl"
      size="sm"
    >
      <nav className={styles.mobileNavLinks}>
        <Button
          className={styles.loginBtn}
          fullWidth
          onClick={() => {
            window.location.href = "/login";
            toggle();
          }}
        >
          Login
        </Button>
      </nav>
    </Drawer>
  );

  const renderHero = () => (
    <section className={styles.hero}>
      <Container size="xl" className={styles.heroContent}>
        <Title className={styles.heroTitle} style={{ marginBottom: 0 }}>
          <strong style={{ opacity: 1 }}>AI-powered</strong> email assistant
        </Title>
        <h1
          className={styles.heroTitle}
          style={{ marginTop: 0, marginBottom: 0 }}
        >
          for lawyers
        </h1>
        <p className={styles.heroSubtitle}>
          Cut out time on low realization and high burnout work.
        </p>
        <Button
          onClick={() => (window.location.href = "/pricing")}
          className={styles.getStartedBtn}
          fz="1.1rem"
          size="md"
          style={{
            backgroundColor: "var(--accent-gold)",
            transition: "background-color 0.3s ease-in-out",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "var(--accent-gold-hover)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "var(--accent-gold)")
          }
          rightSection={<IconArrowRight size={18} />}
        >
          Get Started
        </Button>
      </Container>
    </section>
  );

  const renderFeatures = () => (
    <section id="features" className={styles.features}>
      <Container size="xl" className={styles.featuresContent} ref={featuresRef}>
        <Group justify="center" gap="xl">
          {[
            {
              icon: IconFileDescription,
              title: "Draft legal documents quickly",
              description:
                "AI-powered smart precedents ensure consistent, quality documents.",
              // images: ["/static/msword.svg"]
            },
            {
              icon: IconTools,
              title: "Process attachments easily",
              description:
                "Forward documents to run comparisons, summarize documents, or create PDFs.",
              // images: ["/static/litera.svg"]
            },
            {
              icon: IconDownloadOff,
              title: "No downloads required",
              description:
                "Send emails directly from your inbox, no installation needed.",
              // images: ["/static/outlook.svg", "/static/gmail.svg"]
            },
            // {
            //   icon: IconLock,
            //   title: "Secure Infrastructure",
            //   description: "Built on Google Cloud Platform for scalable infrastructure and AI security",
            //   // images: ["/static/gcp.svg"]
            // }
          ].map((feature, index) => (
            <Transition
              key={index}
              mounted={true}
              transition="slide-up"
              duration={400}
              timingFunction="ease"
            >
              {(transitionStyles) => (
                <Card
                  withBorder
                  shadow="lg"
                  padding="lg"
                  className={`${styles.feature}`}
                  style={{
                    ...transitionStyles,
                    opacity: visibleFeatures[index] ? 1 : 0,
                    transform: visibleFeatures[index]
                      ? "none"
                      : "translateY(20px)",
                  }}
                >
                  <Card.Section p="lg">
                    <feature.icon
                      size={64}
                      stroke={1.5}
                      color="rgba(15,45,45,.9)"
                      className={styles.featureIcon}
                    />
                  </Card.Section>
                  <Title order={4} mt="md">
                    {feature.title}
                  </Title>
                  <Text mt="sm">{feature.description}</Text>
                </Card>
              )}
            </Transition>
          ))}
        </Group>
      </Container>
    </section>
  );

  const partners = [
    {
      logo: "/static/litera.svg",
      name: "Litera",
      description: "High-fidelity document comparison with Litera Compare.",
    },
    {
      logo: "/static/gcp.svg",
      name: "Google Cloud Platform",
      description: "Enterprise-level security infrastructure and encryption.",
    },
    {
      logo: "/static/vertex2.png",
      name: "Google Vertex AI",
      description:
        "Privacy-first LLM platform, available through our secure email interface.",
    },
  ];

  const renderPartners = () => (
    <section className={styles.partners}>
      <Container size="xl">
        <Title order={1} c="var(--primary-color)" align="center" mb="sm">
          Trusted Technology
        </Title>
        <Text align="center" c="dimmed" mb="xl" maw={600} mx="auto">
          Built on best-in-class technology to deliver powerful, secure
          solutions.
        </Text>
        <Group justify="center" gap="lg" mb="lg">
          {partners.map((partner, index) => (
            <Card
              key={index}
              padding="xl"
              radius="md"
              shadow="md"
              withBorder
              className={styles.partnerCard}
            >
              <Stack gap="lg" align="center">
                <img
                  src={partner.logo}
                  alt={partner.name}
                  style={{ height: "28px", width: "auto" }}
                />
                <Text size="sm" c="dimmed" align="center">
                  {partner.description}
                </Text>
              </Stack>
            </Card>
          ))}
        </Group>
        <Group maw={480} gap="xs" justify="center" mx="auto" mt="xl">
          <Text align="center" size="sm" c="dimmed">
            Learn more about our security features and responsible AI
            principles:
          </Text>
          <Group position="center" mt="xs">
            <Button
              variant="light"
              color="gray"
              leftSection={<IconLock size={16} />}
              component="a"
              href="/security"
            >
              Security Center
            </Button>
            <Button
              variant="light"
              color="gray"
              leftSection={<IconFileSearch size={16} />}
              component="a"
              href="/responsible-ai"
            >
              Responsible AI
            </Button>
          </Group>
        </Group>
      </Container>
    </section>
  );

  const renderDemo = () => (
    <section id="demo" className={styles.demoSection} ref={demoRef}>
      <Container size="sm" className={styles.demoContainer}>
        <Title order={1} className={styles.demoTitle} align="center">
          Sample Workflows
        </Title>

        <SegmentedControl
          value={activeDemo}
          onChange={setActiveDemo}
          data={[
            {
              label: (
                <Center>
                  <IconFileDescription size={16} />
                  <Box ml={10}>Draft Documents</Box>
                </Center>
              ),
              value: "SAFE",
            },
            {
              label: (
                <Center>
                  <IconChecklist size={16} />
                  <Box ml={10}>Make Issues Lists</Box>
                </Center>
              ),
              value: "ISSUES_LIST",
            },
            {
              label: (
                <Center>
                  <IconChecklist size={16} />
                  <Box ml={10}>Run Comparisons</Box>
                </Center>
              ),
              value: "REDLINE",
            },
            {
              label: (
                <Center>
                  <IconChecklist size={16} />
                  <Box ml={10}>Ask Questions</Box>
                </Center>
              ),
              value: "QA",
            },
          ]}
          fullWidth
          mb="xl"
        />

        <div className={styles.demoStepsContainer}>
          {demoSteps.map((step, index) => renderDemoStep(step, index))}
        </div>

        {/* <Title order={3} fw={500} className={styles.demoConclusion}>It's that easy.</Title> */}

        <Button
          onClick={() => setIsContactModalOpen(true)}
          className={styles.getStartedBtn}
          mb="xl"
          size="xl"
          fz="1.25rem"
          style={{
            backgroundColor: "var(--accent-gold)",
            transition: "background-color 0.3s ease-in-out",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "var(--accent-gold-hover)")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "var(--accent-gold)")
          }
          rightSection={<IconArrowRight size={20} stroke={2.5} />}
        >
          Learn more
        </Button>
      </Container>
    </section>
  );

  return (
    <MantineProvider theme={theme}>
      <div style={{ position: "relative", minHeight: "100vh" }}>
        <ParticleAnimation />
        {Header()}
        {renderMobileMenu()}
        {renderHero()}
        {renderFeatures()}
        {renderPartners()}
        {renderDemo()}
        <ContactModal
          isOpen={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
        />
        {Footer()}
      </div>
    </MantineProvider>
  );
};

export default SplashLanding;
